import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAdminPresets } from '../../lib/useAdminPresets';
import { Plus, X, Save, MapPin, Plane, Pencil, Check, Percent, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
import { caymanToday } from '../../utils/dateFormat';
import { computeItemTotal, computeDocTotals, formatDiscountBadge } from '../../utils/discountMath';
import { AutoSaveIndicator } from '../../components/admin/AutoSaveIndicator';
import { QuoteTrashNotice } from '../../components/admin/QuoteTrashNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pre-populated services
const PRESET_SERVICES_FALLBACK = {
  "Consultation & Documentation": [
    { name: 'Travel Consultation (30 min)', price: 50 },
    { name: 'Travel Consultation (Additional Hour)', price: 80 },
    { name: 'In-Home Health Certificate', price: 0, note: 'Price varies by destination' },
    { name: 'Cayman Islands Import Permit & Compliance Service (First Pet)', price: 350 },
    { name: 'Cayman Islands Import Permit & Compliance Service (Second Pet)', price: 300 },
    { name: 'Cayman Islands Import Permit & Compliance Service (Third Pet)', price: 250 },
    { name: 'Export Permit Services (First Pet)', price: 500 },
    { name: 'Export Permit Services (Second Pet)', price: 450 },
    { name: 'Export Permit Services (Third Pet)', price: 400 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (First Pet)', price: 85 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (Second Pet)', price: 65 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (Third Pet)', price: 45 },
    { name: 'Pet Passport Application', price: 250 },
    { name: 'IATA Crate/Carrier Compliance Guidance Service', price: 50 },
    { name: 'In-Home Pet Sizing & Measurement Service', price: 75 },
    { name: 'Pet Sizing & IATA Crate/Carrier Compliance Bundle', price: 100 },
    { name: 'Cayman Dog Licensing Convenience Service', price: 75 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (First Pet)', price: 390 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (Second Pet)', price: 340 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (Third Pet)', price: 290 },
    { name: 'Document Delivery Service', price: 0, note: 'Bespoke quotation — based on destination district' }
  ],
  "Travel & Logistics": [
    { name: 'Export Cargo Services', price: 250 },
    { name: 'Flight Arrangements and Airline Coordination Service (First Pet)', price: 175 },
    { name: 'Flight Arrangements and Airline Coordination Service (Additional)', price: 85 },
    { name: 'International Broker Coordination Service (First Pet)', price: 175 },
    { name: 'International Broker Coordination Service (Additional)', price: 85 },
    { name: 'Brokerage Travel Services', price: 0, note: 'Bespoke quotation' },
    { name: 'Cargo & Customs Pre-Clearance (First Pet)', price: 300 },
    { name: 'Cargo & Customs Pre-Clearance (Second Pet)', price: 250 },
    { name: 'Cargo & Customs Pre-Clearance (Third Pet)', price: 200 },
    { name: 'Transportation Coordination Service', price: 0, note: 'Bespoke quotation — pets, kennel weight & location' },
    { name: 'Custom Crate Builder Coordination Service', price: 75 }
  ],
  "Health & Compliance": [
    { name: 'CDC Dog Import Application', price: 50 },
    { name: 'Annual Document Maintenance (First Pet)', price: 100 },
    { name: 'Annual Document Maintenance (Additional)', price: 75 },
    { name: 'Rabies Vaccination Scheduling', price: 25 },
    { name: 'Rabies Vaccination Service (First Pet)', price: 100 },
    { name: 'Rabies Vaccination Service (Additional)', price: 75 },
    { name: 'Microchip Implantation (First Pet)', price: 80 },
    { name: 'Microchip Implantation (Additional)', price: 65 }
  ],
  "Other": [
    { name: 'Custom Service', price: 0 }
  ]
};

export const CreateQuote = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const password = sessionStorage.getItem('admin_password');
  const PRESET_SERVICES = useAdminPresets(PRESET_SERVICES_FALLBACK);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    origin: '',
    destination: '',
    animal_names: '',
    items: [],
    notes: '',
    status: 'draft',
    quote_date: '',
    discount_type: '',      // '' (none) | 'percent' | 'fixed'
    discount_value: 0,
    discount_label: '',
    bank_name: 'Cayman National Bank',
    account_holder: 'Samantha Dorman T/A Pawsome Ventures',
    account_type: 'CI Savings',
    bank_branch: 'Elgin Avenue Branch',
    account_number: '012-86563',
    payment_reference: ''
  });

  const [currentItem, setCurrentItem] = useState({
    description: '',
    quantity: 1,
    unit_price: 0,
    discount_type: '',
    discount_value: 0,
    discount_label: '',
  });

  // Inline edit state
  const [editingItem, setEditingItem] = useState(null);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [quoteLoaded, setQuoteLoaded] = useState(!isEditMode);
  const [trashedQuote, setTrashedQuote] = useState(null);
  const [preserveDraft, setPreserveDraft] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Tracks whether the user has manually edited the Payment Reference field.
  // When false, we auto-fill it with the client's surname (last word of client_name, UPPERCASED).
  const referenceTouchedRef = useRef(false);

  const deriveSurname = (name) =>
    ((name || '').trim().split(/\s+/).pop() || '').toUpperCase();

  // ---- Auto-save (draft only, edit mode only) ----
  // Debounced PUT 15s after the last edit. Never fires on the /new page (we only
  // start autosaving once the user has explicitly hit "Save Draft" and the URL
  // has transitioned to /admin/quotes/{id}/edit).
  const AUTOSAVE_DELAY_MS = 15000;
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const skipNextAutoSaveRef = useRef(true); // skip on initial fetch load
  const savingRef = useRef(false);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    
    if (isEditMode && id) {
      setQuoteLoaded(false);
      setTrashedQuote(null);
      fetchQuote();
    } else if (!isEditMode) {
      setQuoteLoaded(true);
      // Prefill from ?client_email=… (e.g., "New Quote" from a client's page)
      const params = new URLSearchParams(location.search);
      const prefillEmail = params.get('client_email');
      if (prefillEmail) {
        prefillFromClientEmail(prefillEmail);
      }
    }
  }, [id]);

  const prefillFromClientEmail = async (email) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/search?query=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) return;
      const list = await res.json();
      const match = list.find(c => (c.email || '').toLowerCase() === email.toLowerCase()) || list[0];
      if (match) selectClient(match);
    } catch (err) {
      logger.error('Prefill from client_email failed:', err);
    }
  };

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        skipNextAutoSaveRef.current = true;
        setTrashedQuote(data.deleted === true ? data : null);
        // If the persisted quote already has a payment reference, treat it as user-edited
        // so we don't overwrite it when the client name changes.
        if (data.payment_reference) {
          referenceTouchedRef.current = true;
        }
        setFormData({
          client_name: data.client_name || '',
          client_email: data.client_email || '',
          client_phone: data.client_phone || '',
          client_address: data.client_address || '',
          origin: data.origin || '',
          destination: data.destination || '',
          animal_names: data.animal_names || '',
          items: data.items || [],
          notes: data.notes || '',
          status: data.status || 'draft',
          quote_date: (data.quote_date || '').slice(0, 10),
          discount_type: data.discount_type || '',
          discount_value: data.discount_value || 0,
          discount_label: data.discount_label || '',
          bank_name: data.bank_name || 'Cayman National Bank',
          account_holder: data.account_holder || 'Samantha Dorman T/A Pawsome Ventures',
          account_type: data.account_type || 'CI Savings',
          bank_branch: data.bank_branch || 'Elgin Avenue Branch',
          account_number: data.account_number || '012-86563',
          payment_reference: data.payment_reference || ''
        });
        setQuoteLoaded(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to load quote. Return to Quotations and try again.');
      }
    } catch (err) {
      logger.error('Failed to fetch quote:', err);
      setError('Failed to load quote');
    }
  };

  // ---- Auto-save effect: debounced PUT while editing a draft ----
  // Runs only in edit mode and only when the quote is still in draft status.
  // Any change to formData resets the 15s timer; the timer only actually fires
  // if there's no other save in progress.
  useEffect(() => {
    if (!isEditMode || !id) return;                       // never on /new
    if (!quoteLoaded || trashedQuote) return;
    if ((formData.status || '') !== 'draft') return;      // never on sent/paid quotes
    if (skipNextAutoSaveRef.current) {
      // First formData mutation after mount is the initial fetch; skip.
      skipNextAutoSaveRef.current = false;
      return;
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (savingRef.current) return;                      // don't collide with manual save
      saveQuote({ asDraft: true, silent: true });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isEditMode, id, quoteLoaded, trashedQuote]);

  const searchClients = async (query) => {
    if (query.length < 2) {
      setClientSuggestions([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/clients/search?query=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const clients = await response.json();
        setClientSuggestions(clients);
        setShowSuggestions(true);
      }
    } catch (err) {
      logger.error('Failed to search clients:', err);
    }
  };

  const selectClient = (client) => {
    setFormData({
      ...formData,
      client_name: client.name,
      client_email: client.email,
      client_phone: client.phone || '',
      client_address: client.address || '',
      // Auto-fill Payment Reference with surname if user hasn't manually edited it
      payment_reference: referenceTouchedRef.current
        ? formData.payment_reference
        : deriveSurname(client.name),
    });
    setClientSearch(client.name);
    setShowSuggestions(false);
    setClientSuggestions([]);
  };

  const addItem = () => {
    if (!currentItem.description || currentItem.unit_price <= 0) {
      alert('Please enter service description and price');
      return;
    }

    const total = computeItemTotal(currentItem);
    setFormData({
      ...formData,
      items: [...formData.items, { ...currentItem, total, _key: `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}` }]
    });

    setCurrentItem({ description: '', quantity: 1, unit_price: 0, discount_type: '', discount_value: 0, discount_label: '' });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
    if (editingItem && editingItem.index === index) setEditingItem(null);
  };

  const startEditItem = (index) => {
    const it = formData.items[index];
    setEditingItem({
      index,
      description: it.description,
      quantity: it.quantity,
      unit_price: it.unit_price,
      discount_type: it.discount_type || '',
      discount_value: it.discount_value || 0,
      discount_label: it.discount_label || '',
    });
  };

  const saveEditItem = () => {
    if (!editingItem) return;
    if (!editingItem.description || editingItem.unit_price <= 0) {
      alert('Please enter item description and a price greater than zero.');
      return;
    }
    const total = computeItemTotal(editingItem);
    const updated = [...formData.items];
    updated[editingItem.index] = {
      ...updated[editingItem.index],
      description: editingItem.description,
      quantity: editingItem.quantity,
      unit_price: editingItem.unit_price,
      discount_type: editingItem.discount_type || null,
      discount_value: editingItem.discount_value || 0,
      discount_label: editingItem.discount_label || '',
      total,
    };
    setFormData({ ...formData, items: updated });
    setEditingItem(null);
  };

  const cancelEditItem = () => setEditingItem(null);

  const selectPresetService = (serviceName, servicePrice) => {
    setCurrentItem({
      ...currentItem,
      description: serviceName,
      unit_price: servicePrice
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const totals = computeDocTotals(formData.items, {
    discount_type: formData.discount_type,
    discount_value: formData.discount_value,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveQuote({ asDraft: false });
  };

  const saveQuote = async ({ asDraft, silent = false }) => {
    if (savingRef.current || !quoteLoaded || trashedQuote) return;
    setError('');

    if (!asDraft) {
      if (!formData.client_name || !formData.client_email) {
        setError('Client name and email are required');
        return;
      }

      if (!formData.origin || !formData.destination) {
        setError('Origin and destination are required');
        return;
      }

      if (!formData.animal_names) {
        setError('Animal name(s) are required');
        return;
      }

      if (formData.items.length === 0) {
        setError('Please add at least one service');
        return;
      }
    }

    savingRef.current = true;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaving(true);

    try {
      const { subtotal, discountAmount, total } = computeDocTotals(formData.items, {
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
      });
      const quoteData = {
        ...formData,
        // Strip client-side-only _key helper before sending to backend
        items: formData.items.map(({ _key, ...rest }) => ({
          ...rest,
          discount_type: rest.discount_type || null,
          discount_value: rest.discount_value || 0,
        })),
        subtotal,
        discount_type: formData.discount_type || null,
        discount_value: Number(formData.discount_value) || 0,
        discount_amount: discountAmount,
        discount_label: (formData.discount_label || '').trim() || null,
        tax: 0,
        total,
        currency: 'CI$',
        quote_date: formData.quote_date || caymanToday(),
        // Force draft status on Save-Draft; never accidentally promote a partial doc.
        ...(asDraft ? { status: 'draft' } : {}),
      };

      const url = isEditMode
        ? `${API_URL}/api/admin/quotes/${id}`
        : `${API_URL}/api/admin/quotes`;

      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify(quoteData)
      });

      if (response.ok) {
        const result = await response.json();
        if (asDraft) {
          if (!silent) toast.success('Draft saved — you can return to keep editing anytime.');
          if (!isEditMode) {
            // Transition into edit mode so subsequent saves are PUTs on the same doc.
            navigate(`/admin/quotes/${result.id}/edit`, { replace: true });
          }
          setLastAutoSavedAt(new Date());
          setSaving(false);
          return;
        }
        navigate(`/admin/quotes/${isEditMode ? id : result.id}`);
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to save quote');
        if (response.status === 409) {
          setPreserveDraft(true);
          setTrashedQuote({ id, ...formData });
        }
      }
    } catch (err) {
      setError('Failed to save quote. Please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (trashedQuote) {
    return <QuoteTrashNotice quote={trashedQuote} preserveDraft={preserveDraft} onRestored={() => {
      skipNextAutoSaveRef.current = true;
      setTrashedQuote(null);
      setError('');
      toast.success('Quote restored. You can continue editing.');
    }} />;
  }

  if (!quoteLoaded) {
    return <div className="min-h-screen bg-gray-50 p-8" data-testid="quote-editor-loading">
      <p role={error ? 'alert' : 'status'} data-testid="quote-editor-load-status">{error || 'Loading quote…'}</p>
      {error && <button type="button" onClick={() => navigate('/admin/quotes')} data-testid="quote-editor-back"
        className="mt-4 rounded-lg border border-border px-4 py-2">Back to Quotations</button>}
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">{isEditMode ? 'Edit Quote' : 'Create New Quote'}</h1>
              <p className="text-sm text-foreground/60">{isEditMode ? 'Update quote details below' : 'Fill in quote details below'}</p>
            </div>
            <button
              onClick={() => navigate('/admin/quotes')}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Back to Quotes
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          {/* Client Information */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold text-foreground mb-4">Client Information</h2>

            <div className="relative mb-4">
              <label className="block text-sm font-medium text-foreground/70 mb-2">
                Search existing clients
                <span className="text-xs text-foreground/50 ml-2 font-normal">Type name, email or phone to auto-fill</span>
              </label>
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  searchClients(e.target.value);
                }}
                onFocus={() => clientSuggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                data-testid="quote-client-search-input"
                placeholder="Start typing to auto-fill from previous quotes/invoices…"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />

              {showSuggestions && clientSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {clientSuggestions.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => selectClient(client)}
                      data-testid={`quote-client-suggestion-${client.id}`}
                      className="w-full px-4 py-3 text-left hover:bg-primary/5 border-b border-border last:border-b-0"
                    >
                      <div className="font-medium text-foreground">{client.name}</div>
                      <div className="text-sm text-foreground/60">{client.email}{client.phone ? ` · ${client.phone}` : ''}</div>
                      {client.total_invoices > 0 && (
                        <div className="text-xs text-primary mt-1">
                          {client.total_invoices} invoice{client.total_invoices !== 1 ? 's' : ''} · CI${(client.total_spent || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lifetime
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setFormData({
                      ...formData,
                      client_name: newName,
                      // Auto-fill Payment Reference with surname unless the user has edited it
                      payment_reference: referenceTouchedRef.current
                        ? formData.payment_reference
                        : deriveSurname(newName),
                    });
                  }}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Travel Details */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <Plane className="w-5 h-5" />
              Travel Details
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Origin *
                </label>
                <input
                  type="text"
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  required
                  placeholder="e.g., Cayman Islands"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Destination *
                </label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  required
                  placeholder="e.g., United Kingdom"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-2">
                Animal Name(s) *
              </label>
              <input
                type="text"
                value={formData.animal_names}
                onChange={(e) => setFormData({ ...formData, animal_names: e.target.value })}
                required
                placeholder="e.g., Bella, Max (comma-separated for multiple pets)"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-foreground/50 mt-1">Enter names of all animals for this travel quote</p>
            </div>
          </div>

          {/* Services */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold text-foreground mb-4">Services</h2>
            
            {/* Preset Services Dropdown */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-foreground/70 mb-2">Quick Add from Services:</p>
              <select
                onChange={(e) => {
                  const selected = JSON.parse(e.target.value);
                  selectPresetService(selected.name, selected.price);
                  e.target.value = '';
                }}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              >
                <option value="">Select a service...</option>
                {Object.entries(PRESET_SERVICES).map(([category, services]) => (
                  <optgroup key={category} label={category}>
                    {services.map((service, idx) => (
                      <option key={idx} value={JSON.stringify(service)}>{`${service.name} - ${service.price > 0 ? `CI$${service.price}` : service.note}`}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Add Service Form */}
            <div className="grid grid-cols-12 gap-3 sm:gap-4 mb-4">
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-sm font-medium text-foreground/70 mb-2">Service Description</label>
                <input
                  type="text"
                  value={currentItem.description}
                  onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                  placeholder="Service name"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block text-sm font-medium text-foreground/70 mb-2">Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={currentItem.quantity}
                  onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-sm font-medium text-foreground/70 mb-2">Price (CI$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={currentItem.unit_price}
                  onChange={(e) => setCurrentItem({ ...currentItem, unit_price: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="col-span-2 sm:col-span-1 flex items-end">
                <button
                  type="button"
                  onClick={addItem}
                  className="w-full p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  title="Add item"
                >
                  <Plus className="w-5 h-5 mx-auto" />
                </button>
              </div>
              {/* Optional per-item discount */}
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-sm font-medium text-foreground/70 mb-2">Item discount</label>
                <select
                  value={currentItem.discount_type}
                  onChange={(e) => setCurrentItem({ ...currentItem, discount_type: e.target.value })}
                  className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm text-red-700 bg-red-50"
                  data-testid="add-item-discount-type"
                >
                  <option value="">No discount</option>
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed (CI$)</option>
                </select>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-sm font-medium text-foreground/70 mb-2">Discount value</label>
                <input
                  type="number" min="0" step="0.01"
                  value={currentItem.discount_value}
                  onChange={(e) => setCurrentItem({ ...currentItem, discount_value: parseFloat(e.target.value) || 0 })}
                  disabled={!currentItem.discount_type}
                  placeholder={currentItem.discount_type === 'percent' ? 'e.g. 10' : currentItem.discount_type === 'fixed' ? 'e.g. 50.00' : ''}
                  className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm disabled:bg-gray-100"
                  data-testid="add-item-discount-value"
                />
              </div>
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-sm font-medium text-foreground/70 mb-2">Discount label (optional)</label>
                <input
                  type="text"
                  value={currentItem.discount_label}
                  onChange={(e) => setCurrentItem({ ...currentItem, discount_label: e.target.value })}
                  placeholder="e.g. Multi-pet"
                  disabled={!currentItem.discount_type}
                  className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm disabled:bg-gray-100"
                />
              </div>
            </div>

            {/* Added Services List */}
            {formData.items.length > 0 && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-foreground/60">Service</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-foreground/60">Qty</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-foreground/60">Price</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-foreground/60">Total</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {formData.items.map((item, index) => {
                      const isEditing = editingItem && editingItem.index === index;
                      if (isEditing) {
                        return (
                          <tr key={item._key || `existing-${index}`} className="bg-blue-50">
                            <td className="px-4 py-2" colSpan="5">
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={editingItem.description}
                                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                                  placeholder="Description"
                                />
                              <div className="flex gap-2 flex-wrap">
                                <input
                                  type="number"
                                  min="1"
                                  value={editingItem.quantity}
                                  onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 0 })}
                                  className="w-20 px-3 py-2 text-sm border border-border rounded-lg"
                                  placeholder="Qty"
                                />
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editingItem.unit_price}
                                  onChange={(e) => setEditingItem({ ...editingItem, unit_price: parseFloat(e.target.value) || 0 })}
                                  className="flex-1 min-w-[110px] px-3 py-2 text-sm border border-border rounded-lg"
                                  placeholder="Unit price (CI$)"
                                />
                                <select
                                  value={editingItem.discount_type || ''}
                                  onChange={(e) => setEditingItem({ ...editingItem, discount_type: e.target.value })}
                                  className="px-2 py-2 text-xs border border-red-200 rounded-lg text-red-700 bg-red-50"
                                  data-testid={`edit-quote-item-discount-type-${index}`}
                                >
                                  <option value="">No discount</option>
                                  <option value="percent">% off</option>
                                  <option value="fixed">CI$ off</option>
                                </select>
                                {editingItem.discount_type && (
                                  <input
                                    type="number" min="0" step="0.01"
                                    value={editingItem.discount_value || 0}
                                    onChange={(e) => setEditingItem({ ...editingItem, discount_value: parseFloat(e.target.value) || 0 })}
                                    className="w-24 px-2 py-2 text-xs border border-red-200 rounded-lg"
                                    placeholder="Value"
                                    data-testid={`edit-quote-item-discount-value-${index}`}
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={saveEditItem}
                                  className="px-3 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1"
                                  data-testid={`save-edit-quote-item-${index}`}
                                >
                                  <Check className="w-4 h-4" /> Save
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditItem}
                                  className="px-3 py-2 border border-border rounded-lg text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={item._key || `existing-${index}`}>
                          <td className="px-4 py-3 text-sm text-foreground">
                            <div>{item.description}</div>
                            {item.discount_type && item.discount_value > 0 && (
                              <div className="text-xs text-red-600 mt-0.5">
                                {formatDiscountBadge(item)}{item.discount_label ? ` — ${item.discount_label}` : ''}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-foreground/70">{item.quantity}</td>
                          <td className="px-4 py-3 text-sm text-right text-foreground/70">CI${item.unit_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-foreground">CI${item.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => startEditItem(index)}
                                className="p-2 sm:p-1 text-primary hover:bg-primary/10 rounded"
                                title="Edit"
                                data-testid={`edit-quote-item-${index}`}
                              >
                                <Pencil className="w-5 h-5 sm:w-4 sm:h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="p-2 sm:p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Remove"
                              >
                                <X className="w-5 h-5 sm:w-4 sm:h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right font-semibold text-foreground">Subtotal:</td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground">CI${totals.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                    {totals.discountAmount > 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-2 text-right text-red-700">
                          Discount{formData.discount_type === 'percent' ? ` (${formData.discount_value}%)` : ''}{formData.discount_label ? ` — ${formData.discount_label}` : ''}:
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-red-700">
                          −CI${totals.discountAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td></td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right font-bold text-foreground">Total:</td>
                      <td className="px-4 py-3 text-right font-bold text-lg text-primary">CI${totals.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            )}

            {formData.items.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Percent className="w-4 h-4 text-red-600" />
                  <h3 className="font-semibold text-red-700">Overall discount (optional)</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-1">Type</label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                      data-testid="quote-discount-type"
                    >
                      <option value="">None</option>
                      <option value="percent">Percentage</option>
                      <option value="fixed">Fixed amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-1">
                      Value {formData.discount_type === 'percent' ? '(%)' : formData.discount_type === 'fixed' ? '(CI$)' : ''}
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                      disabled={!formData.discount_type}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100"
                      data-testid="quote-discount-value"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-foreground/70 mb-1">Label (optional — shown on PDF)</label>
                    <input
                      type="text"
                      value={formData.discount_label}
                      onChange={(e) => setFormData({ ...formData, discount_label: e.target.value })}
                      placeholder="e.g. Senior citizen, Multi-pet"
                      disabled={!formData.discount_type}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100"
                      data-testid="quote-discount-label"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2" htmlFor="quote-date-input">
                  Quote Date
                </label>
                <input
                  id="quote-date-input"
                  data-testid="quote-date-input"
                  type="date"
                  value={formData.quote_date}
                  onChange={(e) => setFormData({ ...formData, quote_date: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-foreground/50 mt-1">
                  Leave blank on new quotes to use today (Cayman time). The valid-until date is always 30 days later.
                </p>
              </div>
            </div>
            <label className="block text-sm font-medium text-foreground/70 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="4"
              placeholder="Any additional notes or special instructions..."
              lang="en-GB"
              spellCheck="true"
              autoCorrect="on"
              autoCapitalize="sentences"
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Banking Information */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Banking Information</h3>
            <p className="text-sm text-foreground/60 mb-4">This information will appear on the quote PDF for deposit payments</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Cayman National Bank"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Bank Branch
                </label>
                <input
                  type="text"
                  value={formData.bank_branch}
                  onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Elgin Avenue Branch"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Account Holder
                </label>
                <input
                  type="text"
                  value={formData.account_holder}
                  onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Samantha Dorman T/A Pawsome Ventures"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Account Type
                </label>
                <input
                  type="text"
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="CI Savings"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="012-86563"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Payment Reference
                </label>
                <input
                  type="text"
                  value={formData.payment_reference}
                  onChange={(e) => {
                    referenceTouchedRef.current = true;
                    setFormData({ ...formData, payment_reference: e.target.value });
                  }}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. client surname"
                />
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            <AutoSaveIndicator lastAutoSavedAt={lastAutoSavedAt} active={isEditMode && (formData.status || '') === 'draft'} />
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/quotes')}
              className="px-6 py-2 border border-border rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => saveQuote({ asDraft: true })}
              disabled={saving}
              data-testid="save-quote-draft-btn"
              className="px-6 py-2 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              title="Save what you have so far — no required fields, come back later to keep editing"
            >
              <FileText className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Quote' : 'Create Quote')}
            </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
